import { useState, useEffect, useMemo } from "react";
import Layout from "../components/Layout";
import { maintenanceAPI, vehiclesAPI } from "../lib/api";
import { formatVehicleLabel } from "../lib/formatters";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Wrench,
  Plus,
  Edit,
  Trash2,
  Calendar,
  DollarSign,
  Car,
  AlertCircle,
  CheckCircle,
  Clock,
  XCircle,
  Loader2,
  FileText,
  Filter,
  Search,
  CalendarDays,
  Ban,
  Paperclip,
  Upload,
  X
} from "lucide-react";
import { cn } from "../lib/utils";

const MAINTENANCE_TYPES = [
  { value: "PREVENTIVO", label: "Preventivo" },
  { value: "CORRECTIVO", label: "Correctivo" }
];

const MAINTENANCE_STATUSES = [
  { value: "PROGRAMADO", label: "Programado", color: "bg-blue-100 text-blue-800", icon: Clock },
  { value: "EN_PROCESO", label: "En Proceso", color: "bg-yellow-100 text-yellow-800", icon: Wrench },
  { value: "COMPLETADO", label: "Completado", color: "bg-green-100 text-green-800", icon: CheckCircle },
  { value: "CANCELADO", label: "Cancelado", color: "bg-red-100 text-red-800", icon: XCircle }
];

const EXPENSE_CATEGORIES = [
  { value: "REFACCIONES", label: "Refacciones" },
  { value: "MANO_DE_OBRA", label: "Mano de Obra" },
  { value: "SERVICIO", label: "Servicio" },
  { value: "OTRO", label: "Otro" }
];

export default function Mantenimiento() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState("logs");
  const [loading, setLoading] = useState(true);
  const [logs, setLogs] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [blocks, setBlocks] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [stats, setStats] = useState(null);
  
  // v4.55.1: Solo unidades activas para crear nuevos registros.
  // En modo edición: si el registro ya apunta a una unidad ahora inactiva,
  // la incluimos para no perder el dato histórico (con sufijo "(inactiva)" en el render).
  const getVehiclesForForm = (currentVehicleId = null) => {
    const active = vehicles.filter(v => v.status !== "inactivo");
    if (currentVehicleId && !active.find(v => v.id === currentVehicleId)) {
      const historical = vehicles.find(v => v.id === currentVehicleId);
      if (historical) return [...active, historical];
    }
    return active;
  };
  const vehicleSelectLabel = (v) => {
    const base = formatVehicleLabel(v);
    return v?.status === "inactivo" ? `${base} (inactiva)` : base;
  };
  // v4.55.2: Display "Nombre (Placa)" en tablas principales (Bitácoras, Gastos, Bloqueos).
  // Resuelve por unit_id contra vehicles (incluye inactivas). Fallback evita 'undefined'.
  const formatUnitFromRecord = (record) => {
    const v = vehicles.find(x => x.id === record?.unit_id);
    if (v) return formatVehicleLabel(v);
    if (record?.unit_name && record?.unit_plate) return `${record.unit_name} (${record.unit_plate})`;
    if (record?.unit_plate) return record.unit_plate;
    return "Unidad sin identificar";
  };
  
  // Dialog states
  const [logDialogOpen, setLogDialogOpen] = useState(false);
  const [expenseDialogOpen, setExpenseDialogOpen] = useState(false);
  const [blockDialogOpen, setBlockDialogOpen] = useState(false);
  const [editingLog, setEditingLog] = useState(null);
  const [editingExpense, setEditingExpense] = useState(null);
  const [selectedLogForExpenses, setSelectedLogForExpenses] = useState(null);
  const [logExpensesDialogOpen, setLogExpensesDialogOpen] = useState(false);
  
  // Filter states
  const [filters, setFilters] = useState({
    unit_id: "",
    type: "",
    status: "",
    start_date: "",
    end_date: ""
  });
  const [showFilters, setShowFilters] = useState(false);
  
  // Form states
  const [logForm, setLogForm] = useState({
    unit_id: "",
    type: "PREVENTIVO",
    status: "PROGRAMADO",
    start_date: "",
    end_date: "",
    description: "",
    odometer_km: "",
    parts_used: "",
    provider: ""
  });
  
  const [expenseForm, setExpenseForm] = useState({
    unit_id: "",
    maintenance_log_id: "",
    category: "REFACCIONES",
    amount: "",
    vendor_name: "",
    invoice_ref: "",
    description: ""
  });
  
  const [blockForm, setBlockForm] = useState({
    unit_id: "",
    start_date: "",
    end_date: "",
    reason: ""
  });

  // Permission helpers
  const canEdit = user?.role === "admin" || user?.role === "mantenimiento";
  const canDelete = user?.role === "admin";
  const canViewExpenses = user?.role === "admin" || user?.role === "mantenimiento";
  const isReadOnly = user?.role === "logistica" || user?.role === "vendedor";

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [logsRes, blocksRes, vehiclesRes, statsRes] = await Promise.all([
        maintenanceAPI.getLogs({}),
        maintenanceAPI.getBlocks({}),
        vehiclesAPI.getAll(),
        canViewExpenses ? maintenanceAPI.getStats() : Promise.resolve({ data: {} })
      ]);
      
      // Fetch expenses only if user can view them
      let expensesData = [];
      if (canViewExpenses) {
        const expensesRes = await maintenanceAPI.getExpenses({});
        expensesData = expensesRes.data;
      }
      
      setLogs(logsRes.data);
      setExpenses(expensesData);
      setBlocks(blocksRes.data);
      setVehicles(vehiclesRes.data);
      setStats(statsRes.data);
    } catch (error) {
      console.error("Error fetching maintenance data:", error);
      toast.error("Error al cargar datos de mantenimiento");
    } finally {
      setLoading(false);
    }
  };

  // Filtered logs based on filter state
  const filteredLogs = useMemo(() => {
    return logs.filter(log => {
      if (filters.unit_id && log.unit_id !== filters.unit_id) return false;
      if (filters.type && log.type !== filters.type) return false;
      if (filters.status && log.status !== filters.status) return false;
      if (filters.start_date && log.start_date < filters.start_date) return false;
      if (filters.end_date && log.end_date > filters.end_date) return false;
      return true;
    });
  }, [logs, filters]);

  // Filtered blocks based on unit filter
  const filteredBlocks = useMemo(() => {
    if (!filters.unit_id) return blocks;
    return blocks.filter(block => block.unit_id === filters.unit_id);
  }, [blocks, filters.unit_id]);

  const handleCreateLog = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...logForm,
        odometer_km: logForm.odometer_km ? parseInt(logForm.odometer_km) : null
      };
      
      if (editingLog) {
        await maintenanceAPI.updateLog(editingLog.id, payload);
        toast.success("Bitácora actualizada");
      } else {
        await maintenanceAPI.createLog(payload);
        toast.success("Bitácora creada" + (logForm.status === "PROGRAMADO" ? " con bloqueo automático" : ""));
      }
      setLogDialogOpen(false);
      resetLogForm();
      fetchData();
    } catch (error) {
      console.error("[Mantenimiento] handleCreateLog error:", error.response?.status, error.response?.data, "payload:", logForm);
      toast.error(error.response?.data?.detail || "Error al guardar bitácora");
    }
  };

  const handleCreateExpense = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...expenseForm,
        amount: parseFloat(expenseForm.amount),
        maintenance_log_id: expenseForm.maintenance_log_id || null
      };
      
      if (editingExpense) {
        await maintenanceAPI.updateExpense(editingExpense.id, payload);
        toast.success("Gasto actualizado");
      } else {
        await maintenanceAPI.createExpense(payload);
        toast.success("Gasto registrado");
      }
      setExpenseDialogOpen(false);
      resetExpenseForm();
      fetchData();
    } catch (error) {
      console.error("[Mantenimiento] handleCreateExpense error:", error.response?.status, error.response?.data, "payload:", expenseForm);
      toast.error(error.response?.data?.detail || "Error al guardar gasto");
    }
  };

  const handleCreateBlock = async (e) => {
    e.preventDefault();
    try {
      await maintenanceAPI.createBlock(blockForm);
      toast.success("Bloqueo creado (día completo)");
      setBlockDialogOpen(false);
      resetBlockForm();
      fetchData();
    } catch (error) {
      console.error("[Mantenimiento] handleCreateBlock error:", error.response?.status, error.response?.data, "payload:", blockForm);
      toast.error(error.response?.data?.detail || "Error al crear bloqueo");
    }
  };

  const handleDeleteLog = async (id) => {
    if (!window.confirm("¿Eliminar esta bitácora? También se eliminarán los gastos y bloqueos asociados.")) return;
    try {
      await maintenanceAPI.deleteLog(id);
      toast.success("Bitácora eliminada");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al eliminar");
    }
  };

  const handleDeleteExpense = async (id) => {
    if (!window.confirm("¿Eliminar este gasto?")) return;
    try {
      await maintenanceAPI.deleteExpense(id);
      toast.success("Gasto eliminado");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al eliminar");
    }
  };

  const handleDeleteBlock = async (id, hasSource) => {
    if (hasSource) {
      toast.error("Este bloqueo está asociado a una bitácora. Cancele la bitácora para eliminar el bloqueo.");
      return;
    }
    if (!window.confirm("¿Eliminar este bloqueo?")) return;
    try {
      await maintenanceAPI.deleteBlock(id);
      toast.success("Bloqueo eliminado");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al eliminar");
    }
  };

  const resetLogForm = () => {
    setEditingLog(null);
    setLogForm({
      unit_id: "",
      type: "PREVENTIVO",
      status: "PROGRAMADO",
      start_date: "",
      end_date: "",
      description: "",
      odometer_km: "",
      parts_used: "",
      provider: ""
    });
  };

  const resetExpenseForm = () => {
    setEditingExpense(null);
    setExpenseForm({
      unit_id: "",
      maintenance_log_id: "",
      category: "REFACCIONES",
      amount: "",
      vendor_name: "",
      invoice_ref: "",
      description: ""
    });
  };

  const resetBlockForm = () => {
    setBlockForm({
      unit_id: "",
      start_date: "",
      end_date: "",
      reason: ""
    });
  };

  const clearFilters = () => {
    setFilters({
      unit_id: "",
      type: "",
      status: "",
      start_date: "",
      end_date: ""
    });
  };

  const openEditLog = (log) => {
    setEditingLog(log);
    setLogForm({
      unit_id: log.unit_id,
      type: log.type,
      status: log.status,
      start_date: log.start_date,
      end_date: log.end_date,
      description: log.description,
      odometer_km: log.odometer_km || "",
      parts_used: log.parts_used || "",
      provider: log.provider || ""
    });
    setLogDialogOpen(true);
  };

  const openLogExpenses = (log) => {
    setSelectedLogForExpenses(log);
    setLogExpensesDialogOpen(true);
  };

  const openExpenseFormForLog = (log) => {
    setExpenseForm({
      ...expenseForm,
      unit_id: log.unit_id,
      maintenance_log_id: log.id
    });
    setLogExpensesDialogOpen(false);
    setExpenseDialogOpen(true);
  };

  const getStatusBadge = (status) => {
    const statusConfig = MAINTENANCE_STATUSES.find(s => s.value === status);
    if (!statusConfig) return <Badge>{status}</Badge>;
    const Icon = statusConfig.icon;
    return (
      <Badge className={cn(statusConfig.color, "flex items-center gap-1")}>
        <Icon className="w-3 h-3" />
        {statusConfig.label}
      </Badge>
    );
  };

  const getLogExpenses = (logId) => {
    return expenses.filter(e => e.maintenance_log_id === logId);
  };

  const getLogExpenseTotal = (logId) => {
    return getLogExpenses(logId).reduce((sum, e) => sum + (e.amount || 0), 0);
  };

  const formatDateRange = (start, end) => {
    if (start === end) return start;
    return `${start} → ${end}`;
  };

  const activeFiltersCount = Object.values(filters).filter(Boolean).length;

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-6 lg:p-8 space-y-6" data-testid="mantenimiento-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-heading font-bold text-slate-900">Mantenimiento</h1>
            <p className="text-slate-600">Gestión de bitácoras, gastos y bloqueos de unidades</p>
          </div>
          {canEdit && (
            <Button onClick={() => { resetLogForm(); setLogDialogOpen(true); }} data-testid="new-log-btn">
              <Plus className="w-4 h-4 mr-2" />
              Crear Mantenimiento
            </Button>
          )}
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <Clock className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Programados</p>
                    <p className="text-xl font-bold">{stats.logs_by_status?.PROGRAMADO || 0}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-yellow-100 rounded-lg">
                    <Wrench className="w-5 h-5 text-yellow-600" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">En Proceso</p>
                    <p className="text-xl font-bold">{stats.logs_by_status?.EN_PROCESO || 0}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-green-100 rounded-lg">
                    <CheckCircle className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Completados</p>
                    <p className="text-xl font-bold">{stats.logs_by_status?.COMPLETADO || 0}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            {canViewExpenses && (
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-emerald-100 rounded-lg">
                      <DollarSign className="w-5 h-5 text-emerald-600" />
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Gastos Total</p>
                      <p className="text-xl font-bold">${stats.total_expenses?.toLocaleString("es-MX") || 0}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* Filters */}
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-4">
              <Button
                variant="outline"
                onClick={() => setShowFilters(!showFilters)}
                className="flex items-center gap-2"
                data-testid="toggle-filters-btn"
              >
                <Filter className="w-4 h-4" />
                Filtros
                {activeFiltersCount > 0 && (
                  <Badge variant="secondary" className="ml-1">{activeFiltersCount}</Badge>
                )}
              </Button>
              {activeFiltersCount > 0 && (
                <Button variant="ghost" size="sm" onClick={clearFilters}>
                  <X className="w-4 h-4 mr-1" /> Limpiar filtros
                </Button>
              )}
            </div>
            
            {showFilters && (
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4 pt-4 border-t">
                <div className="space-y-2">
                  <Label>Unidad</Label>
                  <Select value={filters.unit_id || "all"} onValueChange={v => setFilters({...filters, unit_id: v === "all" ? "" : v})}>
                    <SelectTrigger data-testid="filter-unit">
                      <SelectValue placeholder="Todas" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todas</SelectItem>
                      {vehicles.map(v => (
                        <SelectItem key={v.id} value={v.id}>{formatVehicleLabel(v)}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Tipo</Label>
                  <Select value={filters.type || "all"} onValueChange={v => setFilters({...filters, type: v === "all" ? "" : v})}>
                    <SelectTrigger data-testid="filter-type">
                      <SelectValue placeholder="Todos" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos</SelectItem>
                      {MAINTENANCE_TYPES.map(t => (
                        <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Status</Label>
                  <Select value={filters.status || "all"} onValueChange={v => setFilters({...filters, status: v === "all" ? "" : v})}>
                    <SelectTrigger data-testid="filter-status">
                      <SelectValue placeholder="Todos" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos</SelectItem>
                      {MAINTENANCE_STATUSES.map(s => (
                        <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Desde</Label>
                  <Input
                    type="date"
                    value={filters.start_date}
                    onChange={e => setFilters({...filters, start_date: e.target.value})}
                    data-testid="filter-start-date"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Hasta</Label>
                  <Input
                    type="date"
                    value={filters.end_date}
                    onChange={e => setFilters({...filters, end_date: e.target.value})}
                    data-testid="filter-end-date"
                  />
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="logs" data-testid="tab-logs">
              <FileText className="w-4 h-4 mr-2" />
              Bitácoras ({filteredLogs.length})
            </TabsTrigger>
            {canViewExpenses && (
              <TabsTrigger value="expenses" data-testid="tab-expenses">
                <DollarSign className="w-4 h-4 mr-2" />
                Gastos ({expenses.length})
              </TabsTrigger>
            )}
            <TabsTrigger value="blocks" data-testid="tab-blocks">
              <Ban className="w-4 h-4 mr-2" />
              Bloqueos ({filteredBlocks.length})
            </TabsTrigger>
          </TabsList>

          {/* Bitácoras Tab */}
          <TabsContent value="logs" className="space-y-4">
            <Card>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Unidad</TableHead>
                    <TableHead>Tipo</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Fechas (día completo)</TableHead>
                    <TableHead>Descripción</TableHead>
                    <TableHead>Km</TableHead>
                    {canViewExpenses && <TableHead>Gastos</TableHead>}
                    {canEdit && <TableHead className="text-right">Acciones</TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredLogs.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={canEdit ? 8 : 7} className="text-center py-8 text-slate-500">
                        No hay bitácoras que coincidan con los filtros
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredLogs.map(log => {
                      const logExpenseTotal = canViewExpenses ? getLogExpenseTotal(log.id) : 0;
                      const logExpenseCount = canViewExpenses ? getLogExpenses(log.id).length : 0;
                      return (
                        <TableRow key={log.id} data-testid={`log-row-${log.id}`}>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <Car className="w-4 h-4 text-slate-400" />
                              <span className="text-sm font-medium">{formatUnitFromRecord(log)}</span>
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline">{log.type}</Badge>
                          </TableCell>
                          <TableCell>{getStatusBadge(log.status)}</TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <CalendarDays className="w-4 h-4 text-slate-400" />
                              <div className="text-sm">
                                <p className="font-medium">{formatDateRange(log.start_date, log.end_date)}</p>
                                <p className="text-xs text-slate-500">Día completo</p>
                              </div>
                            </div>
                          </TableCell>
                          <TableCell className="max-w-xs">
                            <p className="truncate" title={log.description}>{log.description}</p>
                            {log.provider && (
                              <p className="text-xs text-slate-500">Taller: {log.provider}</p>
                            )}
                          </TableCell>
                          <TableCell>{log.odometer_km?.toLocaleString() || "-"}</TableCell>
                          {canViewExpenses && (
                            <TableCell>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => openLogExpenses(log)}
                                className="text-emerald-600 hover:text-emerald-700"
                                data-testid={`view-expenses-${log.id}`}
                              >
                                <DollarSign className="w-4 h-4 mr-1" />
                                ${logExpenseTotal.toLocaleString("es-MX")}
                                {logExpenseCount > 0 && (
                                  <Badge variant="secondary" className="ml-1 text-xs">{logExpenseCount}</Badge>
                                )}
                              </Button>
                            </TableCell>
                          )}
                          {canEdit && (
                            <TableCell className="text-right">
                              <div className="flex items-center justify-end gap-1">
                                <Button size="sm" variant="ghost" onClick={() => openEditLog(log)} title="Editar">
                                  <Edit className="w-4 h-4" />
                                </Button>
                                {canDelete && (
                                  <Button size="sm" variant="ghost" className="text-red-600" onClick={() => handleDeleteLog(log.id)} title="Eliminar">
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                )}
                              </div>
                            </TableCell>
                          )}
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </Card>
          </TabsContent>

          {/* Gastos Tab */}
          {canViewExpenses && (
            <TabsContent value="expenses" className="space-y-4">
              <div className="flex justify-between items-center">
                <h2 className="text-lg font-medium">Gastos de Mantenimiento</h2>
                {canEdit && (
                  <Button onClick={() => { resetExpenseForm(); setExpenseDialogOpen(true); }} data-testid="new-expense-btn">
                    <Plus className="w-4 h-4 mr-2" />
                    Registrar Gasto
                  </Button>
                )}
              </div>

              <Card>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Unidad</TableHead>
                      <TableHead>Bitácora</TableHead>
                      <TableHead>Categoría</TableHead>
                      <TableHead>Monto</TableHead>
                      <TableHead>Proveedor</TableHead>
                      <TableHead>Factura</TableHead>
                      <TableHead>Fecha</TableHead>
                      {canEdit && <TableHead className="text-right">Acciones</TableHead>}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {expenses.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={canEdit ? 8 : 7} className="text-center py-8 text-slate-500">
                          No hay gastos registrados
                        </TableCell>
                      </TableRow>
                    ) : (
                      expenses.map(expense => (
                        <TableRow key={expense.id} data-testid={`expense-row-${expense.id}`}>
                          <TableCell className="text-sm">{formatUnitFromRecord(expense)}</TableCell>
                          <TableCell>
                            {expense.maintenance_log_id ? (
                              <Badge variant="secondary" className="text-xs">Asociado</Badge>
                            ) : (
                              <span className="text-slate-400 text-xs">Sin asociar</span>
                            )}
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline">
                              {EXPENSE_CATEGORIES.find(c => c.value === expense.category)?.label || expense.category}
                            </Badge>
                          </TableCell>
                          <TableCell className="font-medium text-emerald-600">
                            ${expense.amount?.toLocaleString("es-MX")} {expense.currency}
                          </TableCell>
                          <TableCell>{expense.vendor_name || "-"}</TableCell>
                          <TableCell>{expense.invoice_ref || "-"}</TableCell>
                          <TableCell className="text-sm">{expense.created_at?.slice(0, 10)}</TableCell>
                          {canEdit && (
                            <TableCell className="text-right">
                              <Button size="sm" variant="ghost" className="text-red-600" onClick={() => handleDeleteExpense(expense.id)}>
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </TableCell>
                          )}
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </Card>
            </TabsContent>
          )}

          {/* Bloqueos Tab */}
          <TabsContent value="blocks" className="space-y-4">
            <div className="flex justify-between items-center">
              <div>
                <h2 className="text-lg font-medium">Bloqueos de Unidades</h2>
                <p className="text-sm text-slate-500">Unidades no disponibles para servicios en estas fechas</p>
              </div>
              {canEdit && (
                <Button onClick={() => { resetBlockForm(); setBlockDialogOpen(true); }} data-testid="new-block-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Nuevo Bloqueo
                </Button>
              )}
            </div>

            <Card>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Unidad</TableHead>
                    <TableHead>Fechas</TableHead>
                    <TableHead>Duración</TableHead>
                    <TableHead>Razón</TableHead>
                    <TableHead>Origen</TableHead>
                    {canEdit && <TableHead className="text-right">Acciones</TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredBlocks.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={canEdit ? 6 : 5} className="text-center py-8 text-slate-500">
                        No hay bloqueos registrados
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredBlocks.map(block => {
                      // Calculate days
                      const start = new Date(block.start_date);
                      const end = new Date(block.end_date);
                      const days = Math.ceil((end - start) / (1000 * 60 * 60 * 24)) + 1;
                      
                      return (
                        <TableRow key={block.id} data-testid={`block-row-${block.id}`}>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <div className="p-1.5 bg-orange-100 rounded">
                                <Ban className="w-4 h-4 text-orange-600" />
                              </div>
                              <span className="text-sm font-medium">{formatUnitFromRecord(block)}</span>
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <CalendarDays className="w-4 h-4 text-slate-400" />
                              <div className="text-sm">
                                <p className="font-medium">{formatDateRange(block.start_date, block.end_date)}</p>
                              </div>
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge className="bg-orange-100 text-orange-800">
                              {days} día{days > 1 ? "s" : ""} (all-day)
                            </Badge>
                          </TableCell>
                          <TableCell className="max-w-xs truncate">{block.reason}</TableCell>
                          <TableCell>
                            {block.source_ref ? (
                              <Badge variant="secondary" className="flex items-center gap-1 w-fit">
                                <Wrench className="w-3 h-3" />
                                Bitácora
                              </Badge>
                            ) : (
                              <Badge variant="outline">Manual</Badge>
                            )}
                          </TableCell>
                          {canEdit && (
                            <TableCell className="text-right">
                              <Button 
                                size="sm" 
                                variant="ghost" 
                                className="text-red-600" 
                                onClick={() => handleDeleteBlock(block.id, block.source_ref)}
                                disabled={!!block.source_ref}
                                title={block.source_ref ? "Cancele la bitácora para eliminar" : "Eliminar"}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </TableCell>
                          )}
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Log Dialog */}
        <Dialog open={logDialogOpen} onOpenChange={setLogDialogOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>{editingLog ? "Editar Bitácora" : "Nueva Bitácora de Mantenimiento"}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreateLog} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Unidad *</Label>
                  <Select value={logForm.unit_id} onValueChange={v => setLogForm({...logForm, unit_id: v})} required>
                    <SelectTrigger data-testid="log-unit-select">
                      <SelectValue placeholder="Seleccionar..." />
                    </SelectTrigger>
                    <SelectContent>
                      {getVehiclesForForm(logForm.unit_id).map(v => (
                        <SelectItem key={v.id} value={v.id}>{vehicleSelectLabel(v)}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Tipo *</Label>
                  <Select value={logForm.type} onValueChange={v => setLogForm({...logForm, type: v})}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {MAINTENANCE_TYPES.map(t => (
                        <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Status</Label>
                  <Select value={logForm.status} onValueChange={v => setLogForm({...logForm, status: v})}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {MAINTENANCE_STATUSES.map(s => (
                        <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Kilometraje</Label>
                  <Input
                    type="number"
                    value={logForm.odometer_km}
                    onChange={e => setLogForm({...logForm, odometer_km: e.target.value})}
                    placeholder="45000"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Fecha Inicio (día completo) *</Label>
                  <Input
                    type="date"
                    value={logForm.start_date}
                    onChange={e => setLogForm({...logForm, start_date: e.target.value})}
                    required
                    data-testid="log-start-date"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Fecha Fin (día completo) *</Label>
                  <Input
                    type="date"
                    value={logForm.end_date}
                    onChange={e => setLogForm({...logForm, end_date: e.target.value})}
                    required
                    data-testid="log-end-date"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Descripción *</Label>
                <Textarea
                  value={logForm.description}
                  onChange={e => setLogForm({...logForm, description: e.target.value})}
                  placeholder="Descripción del mantenimiento..."
                  required
                  data-testid="log-description"
                />
              </div>

              <div className="space-y-2">
                <Label>Refacciones/Partes</Label>
                <Textarea
                  value={logForm.parts_used}
                  onChange={e => setLogForm({...logForm, parts_used: e.target.value})}
                  placeholder="Aceite 10W40, Filtro de aceite..."
                  rows={2}
                />
              </div>

              <div className="space-y-2">
                <Label>Proveedor/Taller</Label>
                <Input
                  value={logForm.provider}
                  onChange={e => setLogForm({...logForm, provider: e.target.value})}
                  placeholder="Taller interno / Nombre taller externo"
                />
              </div>

              {logForm.status === "PROGRAMADO" && !editingLog && (
                <div className="p-3 bg-blue-50 rounded-lg text-sm text-blue-700 flex items-start gap-2">
                  <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <span>Se creará automáticamente un <strong>bloqueo de unidad</strong> para estas fechas (día completo). La unidad no podrá asignarse a contratos durante este período.</span>
                </div>
              )}

              {editingLog && logForm.status === "CANCELADO" && editingLog.status !== "CANCELADO" && (
                <div className="p-3 bg-amber-50 rounded-lg text-sm text-amber-700 flex items-start gap-2">
                  <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <span>Al cambiar el status a "Cancelado", se eliminará el bloqueo de unidad asociado.</span>
                </div>
              )}

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setLogDialogOpen(false)}>
                  Cancelar
                </Button>
                <Button type="submit" data-testid="save-log-btn">
                  {editingLog ? "Guardar Cambios" : "Crear Bitácora"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Expense Dialog */}
        <Dialog open={expenseDialogOpen} onOpenChange={setExpenseDialogOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Registrar Gasto de Mantenimiento</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreateExpense} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Unidad *</Label>
                  <Select value={expenseForm.unit_id} onValueChange={v => setExpenseForm({...expenseForm, unit_id: v})} required>
                    <SelectTrigger>
                      <SelectValue placeholder="Seleccionar..." />
                    </SelectTrigger>
                    <SelectContent>
                      {getVehiclesForForm(expenseForm.unit_id).map(v => (
                        <SelectItem key={v.id} value={v.id}>{vehicleSelectLabel(v)}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Categoría *</Label>
                  <Select value={expenseForm.category} onValueChange={v => setExpenseForm({...expenseForm, category: v})}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {EXPENSE_CATEGORIES.map(c => (
                        <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Monto *</Label>
                  <Input
                    type="number"
                    step="any"
                    value={expenseForm.amount}
                    onChange={e => setExpenseForm({...expenseForm, amount: e.target.value})}
                    placeholder="1500.00"
                    required
                    data-testid="expense-amount"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Bitácora (opcional)</Label>
                  <Select value={expenseForm.maintenance_log_id || "none"} onValueChange={v => setExpenseForm({...expenseForm, maintenance_log_id: v === "none" ? "" : v})}>
                    <SelectTrigger>
                      <SelectValue placeholder="Sin asociar" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Sin asociar</SelectItem>
                      {logs.map(l => (
                        <SelectItem key={l.id} value={l.id}>{l.unit_plate} - {l.description?.slice(0, 30)}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Proveedor</Label>
                  <Input
                    value={expenseForm.vendor_name}
                    onChange={e => setExpenseForm({...expenseForm, vendor_name: e.target.value})}
                    placeholder="Nombre del proveedor"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Referencia Factura</Label>
                  <Input
                    value={expenseForm.invoice_ref}
                    onChange={e => setExpenseForm({...expenseForm, invoice_ref: e.target.value})}
                    placeholder="FAC-001"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Descripción</Label>
                <Textarea
                  value={expenseForm.description}
                  onChange={e => setExpenseForm({...expenseForm, description: e.target.value})}
                  placeholder="Detalle del gasto..."
                  rows={2}
                />
              </div>

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setExpenseDialogOpen(false)}>
                  Cancelar
                </Button>
                <Button type="submit" data-testid="save-expense-btn">
                  Registrar Gasto
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Block Dialog */}
        <Dialog open={blockDialogOpen} onOpenChange={setBlockDialogOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Nuevo Bloqueo de Unidad</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreateBlock} className="space-y-4">
              <div className="p-3 bg-orange-50 rounded-lg text-sm text-orange-700 flex items-start gap-2">
                <Ban className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <span>Los bloqueos son por <strong>día completo</strong>. La unidad no podrá ser asignada a contratos en estas fechas.</span>
              </div>

              <div className="space-y-2">
                <Label>Unidad *</Label>
                <Select value={blockForm.unit_id} onValueChange={v => setBlockForm({...blockForm, unit_id: v})} required>
                  <SelectTrigger data-testid="block-unit-select">
                    <SelectValue placeholder="Seleccionar..." />
                  </SelectTrigger>
                  <SelectContent>
                    {getVehiclesForForm(blockForm.unit_id).map(v => (
                      <SelectItem key={v.id} value={v.id}>{vehicleSelectLabel(v)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Fecha Inicio *</Label>
                  <Input
                    type="date"
                    value={blockForm.start_date}
                    onChange={e => setBlockForm({...blockForm, start_date: e.target.value})}
                    required
                    data-testid="block-start-date"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Fecha Fin *</Label>
                  <Input
                    type="date"
                    value={blockForm.end_date}
                    onChange={e => setBlockForm({...blockForm, end_date: e.target.value})}
                    required
                    data-testid="block-end-date"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Razón *</Label>
                <Textarea
                  value={blockForm.reason}
                  onChange={e => setBlockForm({...blockForm, reason: e.target.value})}
                  placeholder="Motivo del bloqueo..."
                  required
                  data-testid="block-reason"
                />
              </div>

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setBlockDialogOpen(false)}>
                  Cancelar
                </Button>
                <Button type="submit" data-testid="save-block-btn">
                  Crear Bloqueo
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Log Expenses Dialog */}
        <Dialog open={logExpensesDialogOpen} onOpenChange={setLogExpensesDialogOpen}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Gastos de Bitácora</DialogTitle>
            </DialogHeader>
            {selectedLogForExpenses && (
              <div className="space-y-4">
                <div className="p-4 bg-slate-50 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-500">Unidad</p>
                      <p className="font-medium">{selectedLogForExpenses.unit_plate} - {selectedLogForExpenses.unit_name}</p>
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Fechas</p>
                      <p className="font-medium">{formatDateRange(selectedLogForExpenses.start_date, selectedLogForExpenses.end_date)}</p>
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Status</p>
                      {getStatusBadge(selectedLogForExpenses.status)}
                    </div>
                  </div>
                  <p className="mt-2 text-sm text-slate-600">{selectedLogForExpenses.description}</p>
                </div>

                <div className="flex items-center justify-between">
                  <h3 className="font-medium">Gastos Asociados</h3>
                  {canEdit && (
                    <Button size="sm" variant="outline" onClick={() => openExpenseFormForLog(selectedLogForExpenses)}>
                      <Plus className="w-4 h-4 mr-1" />
                      Agregar Gasto
                    </Button>
                  )}
                </div>

                {getLogExpenses(selectedLogForExpenses.id).length === 0 ? (
                  <div className="text-center py-8 text-slate-500 border rounded-lg">
                    No hay gastos registrados para esta bitácora
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Categoría</TableHead>
                        <TableHead>Monto</TableHead>
                        <TableHead>Proveedor</TableHead>
                        <TableHead>Factura</TableHead>
                        <TableHead>Fecha</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {getLogExpenses(selectedLogForExpenses.id).map(expense => (
                        <TableRow key={expense.id}>
                          <TableCell>
                            <Badge variant="outline">
                              {EXPENSE_CATEGORIES.find(c => c.value === expense.category)?.label || expense.category}
                            </Badge>
                          </TableCell>
                          <TableCell className="font-medium text-emerald-600">
                            ${expense.amount?.toLocaleString("es-MX")}
                          </TableCell>
                          <TableCell>{expense.vendor_name || "-"}</TableCell>
                          <TableCell>{expense.invoice_ref || "-"}</TableCell>
                          <TableCell>{expense.created_at?.slice(0, 10)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}

                <div className="flex justify-between items-center pt-4 border-t">
                  <p className="text-lg font-medium">Total:</p>
                  <p className="text-xl font-bold text-emerald-600">
                    ${getLogExpenseTotal(selectedLogForExpenses.id).toLocaleString("es-MX")} MXN
                  </p>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
